// Admin panel: season sponsors / prizes.
//
// Sponsors are grouped by `year` (= season). The Quiz tab on the public
// side fetches /api/quiz-sponsors?year=<current-year> and renders a
// "Ceny v sezóně" section. Sort order controls vertical position; ties
// fall back to creation date so the panel stays stable.

import { useEffect, useState, type FC } from "react";
import {
  adminSaveQuizSponsor, adminDeleteQuizSponsor, fetchQuizSponsors,
  type QuizSponsor, type AdminUser,
} from "../../api";

export const SponsorsView: FC<{ token: string; admin: AdminUser }> = ({ token }) => {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState<number>(currentYear);
  const [sponsors, setSponsors] = useState<QuizSponsor[]>([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<QuizSponsor | null>(null);

  useEffect(() => {
    let mounted = true;
    fetchQuizSponsors(year).then((list) => { if (mounted) setSponsors(list); });
    return () => { mounted = false; };
  }, [year, reloadKey]);

  const remove = async (id: string, name: string) => {
    if (!window.confirm(`Smazat sponzora "${name}"?`)) return;
    const r = await adminDeleteQuizSponsor(token, id);
    if (!r.ok) { alert(r.error ?? "Selhalo."); return; }
    setReloadKey((k) => k + 1);
  };

  return (
    <div>
      <div className="row-actions" style={{ marginBottom: "0.9rem" }}>
        <button
          className="btn btn-primary"
          onClick={() => { setAdding(true); setEditing(null); }}
        >+ Nový sponzor</button>
        <button className="btn btn-ghost btn-sm" onClick={() => setReloadKey((k) => k + 1)}>Obnovit</button>
        <label className="small muted" style={{ marginLeft: "auto" }}>
          Sezóna&nbsp;
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
            {[currentYear - 1, currentYear, currentYear + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
      </div>

      {(adding || editing) && (
        <SponsorForm
          token={token}
          year={year}
          {...(editing ? { initial: editing } : {})}
          onSaved={() => { setAdding(false); setEditing(null); setReloadKey((k) => k + 1); }}
          onCancel={() => { setAdding(false); setEditing(null); }}
        />
      )}

      {sponsors.length === 0 ? (
        <div className="empty-state"><p>Pro {year} zatím žádný sponzor.</p></div>
      ) : (
        sponsors.map((s) => (
          <div key={s.id} className="card admin-quiz-card">
            <div className="flex-row space-between" style={{ alignItems: "flex-start", gap: "0.75rem" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 700 }}>{s.name}</div>
                {s.prizeLabel && <div className="small">{s.prizeLabel}</div>}
                <div className="small muted">
                  Pořadí: {s.sortOrder}
                  {s.sponsorUrl && <> · <a href={s.sponsorUrl} target="_blank" rel="noreferrer">odkaz</a></>}
                  {s.logoUrl && <> · logo OK</>}
                </div>
              </div>
              <div className="row-actions" style={{ marginTop: 0, gap: "0.4rem", flexDirection: "column", alignItems: "flex-end" }}>
                <button className="btn btn-sm btn-ghost" onClick={() => { setEditing(s); setAdding(false); }}>Upravit</button>
                <button className="btn btn-sm btn-danger" onClick={() => remove(s.id, s.name)}>Smazat</button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

const SponsorForm: FC<{
  token: string;
  year: number;
  initial?: QuizSponsor;
  onSaved: () => void;
  onCancel: () => void;
}> = ({ token, year, initial, onSaved, onCancel }) => {
  const [name, setName] = useState(initial?.name ?? "");
  const [logoUrl, setLogoUrl] = useState(initial?.logoUrl ?? "");
  const [prizeLabel, setPrizeLabel] = useState(initial?.prizeLabel ?? "");
  const [sponsorUrl, setSponsorUrl] = useState(initial?.sponsorUrl ?? "");
  const [sortOrder, setSortOrder] = useState(String(initial?.sortOrder ?? 0));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    if (!name.trim()) { setErr("Jméno je povinné."); return; }
    setBusy(true); setErr("");
    const r = await adminSaveQuizSponsor(token, {
      ...(initial?.id ? { id: initial.id } : {}),
      name: name.trim(),
      logoUrl: logoUrl.trim() || null,
      prizeLabel: prizeLabel.trim() || null,
      sponsorUrl: sponsorUrl.trim() || null,
      year: initial?.year ?? year,
      sortOrder: Number(sortOrder) || 0,
    });
    setBusy(false);
    if (!r.ok) { setErr(r.error ?? "Selhalo uložení."); return; }
    onSaved();
  };

  return (
    <div className="card" style={{ marginBottom: "1rem" }}>
      <h3>{initial ? "Upravit sponzora" : "Nový sponzor"}</h3>
      <div className="form">
        <div className="field">
          <label>Jméno *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </div>
        <div className="field">
          <label>Cena (text)</label>
          <input
            value={prizeLabel}
            onChange={(e) => setPrizeLabel(e.target.value)}
            placeholder="např. 100 000 sats za sezónu"
          />
        </div>
        <div className="flex-row" style={{ gap: "0.7rem" }}>
          <div className="field" style={{ flex: 1 }}>
            <label>Logo URL</label>
            <input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://…" />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label>Stránka sponzora</label>
            <input value={sponsorUrl} onChange={(e) => setSponsorUrl(e.target.value)} placeholder="https://…" />
          </div>
        </div>
        <div className="field" style={{ maxWidth: 180 }}>
          <label>Pořadí (menší = výš)</label>
          <input
            type="number"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          />
        </div>
        {err && <p className="error">{err}</p>}
        <div className="row-actions">
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            {busy ? "Ukládám…" : "Uložit"}
          </button>
          <button className="btn btn-ghost" onClick={onCancel}>Zrušit</button>
        </div>
      </div>
    </div>
  );
};
